import React, { useState, useEffect, useMemo } from "react";
import Joyride, { STATUS } from "react-joyride";
import { useNavigate } from "react-router";
import { Sidebar } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/ui/dashboard/dashboard-header";
import { HeroBanner } from "@/components/ui/dashboard/hero-banner";
import { DashboardStatsGrid } from "@/components/ui/dashboard/dashboard-stats-grid";
import { RecommendedCareerPaths } from "@/components/ui/dashboard/recommended-career-paths";
import { ProfileCard } from "@/components/ui/profile/profile-card";
import { GradesChart } from "@/components/ui/dashboard/grades-chart";
import { AchievementsGrid } from "@/components/ui/dashboard/achievements-card";
import { UniversalSearch } from "@/components/ui/universal-search";
import { NotesEditorModal } from "@/components/ui/curriculum/notes-editor-modal";
import ProtectedRoute from "@/components/ProtectedRoute";
import { useUserData } from "@/hooks/useUserData";
import { useThemeStore } from "@/hooks/stores/themeStore";
import { StudentStore } from "@/hooks/stores/StudentStore";
import { BookOpen, Gamepad2, ClipboardList, TrendingUp, Target, ChevronRight, Sparkles, Brain, CalendarDays, MapPin, Atom, Palette, Briefcase } from "lucide-react";
import { listEvents, rsvpEvent } from "@/hooks/api/schoolApi";
import config from "../../config";
import traitListData from "../../traitList.json";

// ─── Natural Talent Snapshot ──────────────────────────────────────────────────

// Map trait cluster → display info
const CLUSTER_META = {
  STEM:       { label: "STEM",     color: "#6366f1", bg: "bg-indigo-50",  text: "text-indigo-600",  Icon: Atom      },
  Management: { label: "Business", color: "#f59e0b", bg: "bg-amber-50",   text: "text-amber-600",   Icon: Briefcase },
  Humanities: { label: "Arts",     color: "#10b981", bg: "bg-emerald-50", text: "text-emerald-600", Icon: Palette   },
};

// Build lookup: traitId → cluster
const TRAIT_CLUSTER = Object.fromEntries(
  traitListData.filter(t => CLUSTER_META[t.cluster]).map(t => [t.id, t.cluster])
);

function computeClusterScores(traitProfile) {
  const buckets = { STEM: [], Management: [], Humanities: [] };
  Object.entries(traitProfile).forEach(([id, score]) => {
    const cluster = TRAIT_CLUSTER[id];
    if (cluster && buckets[cluster]) buckets[cluster].push(score);
  });
  return Object.fromEntries(
    Object.entries(buckets).map(([c, scores]) => [
      c, scores.length ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : 0,
    ])
  );
}

function TraitSnapshot({ traitProfile, navigate }) {
  if (!traitProfile || Object.keys(traitProfile).length === 0) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-4 flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-center shrink-0">
          <Brain size={18} className="text-indigo-500" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-slate-800 font-bold text-sm">Discover your natural talent</p>
          <p className="text-slate-500 text-xs mt-0.5">Take the psychometric assessment to reveal your STEM, Business or Arts strengths.</p>
        </div>
        <button
          onClick={() => navigate("/psychometric-assessment")}
          className="shrink-0 flex items-center gap-1 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold px-3 py-1.5 rounded-lg transition-all"
        >
          Start <ChevronRight size={12} />
        </button>
      </div>
    );
  }

  const clusterScores = computeClusterScores(traitProfile);
  const sorted = Object.entries(clusterScores).sort(([, a], [, b]) => b - a);
  const [dominantCluster, dominantScore] = sorted[0];
  const dominant = CLUSTER_META[dominantCluster];
  const DominantIcon = dominant.Icon;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-4">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles size={15} className="text-indigo-500 shrink-0" />
        <h3 className="text-slate-900 font-bold text-sm">Natural Talent Profile</h3>
        <button
          onClick={() => navigate("/assessment-results")}
          className="ml-auto text-xs text-slate-400 hover:text-indigo-600 transition-colors font-medium"
        >
          View all →
        </button>
      </div>

      {/* Dominant strength badge */}
      <div className={`flex items-center gap-2.5 rounded-xl px-3 py-2.5 mb-3 ${dominant.bg}`}>
        <div className={`w-7 h-7 rounded-lg ${dominant.bg} border border-current/10 flex items-center justify-center shrink-0`}>
          <DominantIcon size={15} className={dominant.text} />
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-[10px] font-black uppercase tracking-widest ${dominant.text}`}>Dominant Strength</p>
          <p className="text-slate-900 font-bold text-sm leading-tight">{dominant.label}</p>
        </div>
        <span className={`text-lg font-black ${dominant.text}`}>{dominantScore}</span>
      </div>

      {/* Cluster breakdown bars */}
      <div className="space-y-1.5">
        {sorted.map(([cluster, score]) => {
          const meta = CLUSTER_META[cluster];
          return (
            <div key={cluster} className="flex items-center gap-2">
              <div className="text-[11px] text-slate-500 w-14 leading-none font-medium">{meta.label}</div>
              <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-700"
                  style={{ width: `${score}%`, backgroundColor: meta.color }}
                />
              </div>
              <div className="text-[11px] font-bold text-slate-700 w-6 text-right leading-none">{score}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Career Suggestion Cards ──────────────────────────────────────────────────
const BROAD_ICON = {
  broad_stem_tech: "⚙️", broad_health_medical: "🩺", broad_creative_arts: "🎨",
  broad_business_finance: "📊", broad_humanities_social: "📚",
  broad_law_governance: "⚖️", broad_education_training: "🎓", broad_agriculture_environment: "🌱",
};

function SuggestionCard({ suggestion, navigate }) {
  const pct = Math.round(suggestion.matchScore * 100);
  const isPositive = suggestion.type === "positive";
  const metCount = suggestion.metThresholdCount;
  const total = suggestion.totalTraitCount;

  // gap traits — ones not meeting threshold
  const gaps = suggestion.traitDistances
    .filter(t => t.distance < 0)
    .sort((a, b) => a.distance - b.distance)
    .slice(0, 3);

  return (
    <div className={`rounded-xl border p-4 transition-all ${
      isPositive
        ? "border-emerald-500/30 bg-emerald-500/5"
        : "border-slate-600/30 bg-white/3"
    }`}>
      <div className="flex items-start justify-between mb-2">
        <div>
          <div className="text-xs text-slate-400 mb-0.5">{suggestion.node.alias}</div>
          <div className="text-white font-bold text-sm">{suggestion.node.name}</div>
        </div>
        <div className={`text-xl font-black ${isPositive ? "text-emerald-400" : "text-slate-400"}`}>
          {pct}%
        </div>
      </div>
      <div className="flex items-center gap-2 mb-3">
        <div className="text-xs text-slate-400">{metCount}/{total} traits met</div>
        {isPositive
          ? <span className="text-xs bg-emerald-500/20 text-emerald-300 px-2 py-0.5 rounded-full">Strong match</span>
          : <span className="text-xs bg-slate-500/20 text-slate-400 px-2 py-0.5 rounded-full">Near miss</span>
        }
      </div>
      {gaps.length > 0 && (
        <div className="text-xs text-slate-500">
          Gaps: {gaps.map(g => g.name).join(" · ")}
        </div>
      )}
    </div>
  );
}

function CareerSuggestionsPanel({ suggestions, navigate }) {
  if (!suggestions || suggestions.length === 0) return null;

  const positives = suggestions.filter(s => s.type === "positive");
  const nearMisses = suggestions.filter(s => s.type === "near-miss");

  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm p-5">
      <div className="flex items-center gap-2 mb-4">
        <Sparkles size={18} className="text-amber-400" />
        <h3 className="text-white font-bold text-sm">Your Career Matches</h3>
        <span className="ml-auto text-xs text-slate-500">{suggestions.length} fields matched</span>
      </div>
      {positives.length > 0 && (
        <div className="mb-4">
          <div className="text-xs text-emerald-400 font-semibold mb-2">Strong Matches</div>
          <div className="space-y-2">
            {positives.slice(0, 3).map(s => (
              <SuggestionCard key={s.node.nodeId} suggestion={s} navigate={navigate} />
            ))}
          </div>
        </div>
      )}
      {nearMisses.length > 0 && (
        <div>
          <div className="text-xs text-slate-400 font-semibold mb-2">Close — Keep Building</div>
          <div className="space-y-2">
            {nearMisses.slice(0, 2).map(s => (
              <SuggestionCard key={s.node.nodeId} suggestion={s} navigate={navigate} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Upcoming Events ──────────────────────────────────────────────────────────
const TYPE_COLORS = {
  academic: "bg-indigo-100 text-indigo-700",
  sports: "bg-teal-100 text-teal-700",
  cultural: "bg-amber-100 text-amber-700",
  career: "bg-violet-100 text-violet-700",
  workshop: "bg-rose-100 text-rose-700",
  general: "bg-slate-100 text-slate-700",
};

function UpcomingEvents() {
  const [events, setEvents] = React.useState([]);
  const [rsvpd, setRsvpd] = React.useState(new Set());

  React.useEffect(() => {
    if (config.appMode === "prototype") {
      setEvents([
        { _id: "e1", title: "Career Day 2025", type: "career", startDate: new Date(Date.now() + 3 * 86400000).toISOString(), location: "Main Hall", rsvpEnabled: true },
        { _id: "e2", title: "Mathematics Quiz Competition", type: "academic", startDate: new Date(Date.now() + 7 * 86400000).toISOString(), isOnline: true, rsvpEnabled: true },
        { _id: "e3", title: "Inter-House Sports", type: "sports", startDate: new Date(Date.now() + 14 * 86400000).toISOString(), location: "Sports Field" },
      ]);
      return;
    }
    listEvents({ limit: 5 })
      .then(({ events: items }) => setEvents(items ?? []))
      .catch(() => {});
  }, []);

  if (events.length === 0) return null;

  const handleRsvp = (id) => {
    rsvpEvent(id).catch(() => {});
    setRsvpd((prev) => new Set([...prev, id]));
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-4 space-y-3">
      <div className="flex items-center gap-2 mb-1">
        <CalendarDays size={16} className="text-indigo-500" />
        <h3 className="text-slate-800 font-bold text-sm">Upcoming Events</h3>
      </div>
      {events.slice(0, 3).map((event) => {
        const colorCls = TYPE_COLORS[event.type] ?? TYPE_COLORS.general;
        const date = new Date(event.startDate);
        const dateStr = date.toLocaleDateString("en-NG", { month: "short", day: "numeric" });
        return (
          <div key={event._id} className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-xl bg-indigo-50 flex flex-col items-center justify-center shrink-0 text-indigo-700">
              <span className="text-xs font-bold leading-none">{dateStr.split(" ")[0]}</span>
              <span className="text-base font-black leading-none">{dateStr.split(" ")[1]}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-800 truncate">{event.title}</p>
              <div className="flex items-center gap-2 mt-0.5">
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${colorCls}`}>{event.type}</span>
                {event.location && (
                  <span className="flex items-center gap-0.5 text-[10px] text-slate-400">
                    <MapPin size={9} /> {event.location}
                  </span>
                )}
                {event.isOnline && <span className="text-[10px] text-teal-600 font-semibold">Online</span>}
              </div>
            </div>
            {event.rsvpEnabled && !rsvpd.has(event._id) && (
              <button
                onClick={() => handleRsvp(event._id)}
                className="shrink-0 text-[10px] font-bold text-indigo-600 border border-indigo-200 px-2 py-1 rounded-lg hover:bg-indigo-50"
              >
                RSVP
              </button>
            )}
            {rsvpd.has(event._id) && (
              <span className="shrink-0 text-[10px] font-bold text-teal-600">Going</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Quick Actions ────────────────────────────────────────────────────────────
function QuickActions({ navigate }) {
  const actions = [
    {
      icon: BookOpen,
      label: "Take a Course",
      sub: "Micro lessons",
      cardCls: "bg-indigo-600 hover:bg-indigo-700 shadow-indigo-200",
      to: "/career-learning-path",
    },
    {
      icon: ClipboardList,
      label: "Log Activity",
      sub: "Real-world wins",
      cardCls: "bg-teal-600 hover:bg-teal-700 shadow-teal-200",
      to: "/activity-log",
    },
    {
      icon: Gamepad2,
      label: "Play a Game",
      sub: "Earn trait XP",
      cardCls: "bg-amber-500 hover:bg-amber-600 shadow-amber-200",
      to: "/games",
    },
  ];

  return (
    <div className="rounded-2xl border border-slate-200/20 bg-white/20 p-5 shadow-sm">
      <div className="flex items-center gap-2 mb-4">
        <Target size={18} className="text-indigo-600" />
        <h3 className="text-slate-800 font-bold text-sm">Quick Actions</h3>
      </div>
      <div className="flex flex-col sm:flex-row gap-3">
        {actions.map(({ icon: Icon, label, sub, cardCls, to }) => (
          <button
            key={to}
            onClick={() => navigate(to)}
            className={`flex-1 flex items-center gap-3 p-4 rounded-2xl shadow-lg ${cardCls} transition-all hover:scale-[1.02] active:scale-[0.97]`}
          >
            <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
              <Icon size={18} className="text-white" />
            </div>
            <div className="text-left min-w-0">
              <div className="text-white text-sm font-bold truncate">{label}</div>
              <div className="text-white/80 text-xs truncate">{sub}</div>
            </div>
            <ChevronRight size={14} className="text-white/60 ml-auto shrink-0" />
          </button>
        ))}
      </div>
    </div>
  );
}

const Animated3DTooltip = ({
  index,
  step,
  backProps,
  primaryProps,
  skipProps,
  tooltipProps,
  isLastStep,
}) => (
  <div
    {...tooltipProps}
    className="tour-3d-box"
    style={{
      backgroundColor: "#ffffff",
      padding: "28px",
      borderRadius: "40px",
      border: "1px solid rgba(255, 255, 255, 0.5)",
      // Layered shadows for that "floating 3D" look
      boxShadow: `
        0 20px 40px rgba(22, 154, 144, 0.15),
        0 0 0 8px rgba(22, 154, 144, 0.05),
        inset 0 -8px 0 rgba(0, 0, 0, 0.05)
      `,
      maxWidth: "380px",
      position: "relative",
      overflow: "hidden",
    }}
  >
    {/* Decorative 3D Blob Backgrounds */}
    <div
      style={{
        position: "absolute",
        top: "-20px",
        right: "-20px",
        width: "80px",
        height: "80px",
        background: "linear-gradient(135deg, #6366f1, #818cf8)",
        borderRadius: "50%",
        opacity: 0.1,
        zIndex: 0,
      }}
    />

    <div style={{ position: "relative", zIndex: 1 }}>
      {step.title && (
        <h3
          style={{
            color: "#1e293b",
            fontSize: "22px",
            fontWeight: "900",
            marginBottom: "10px",
            letterSpacing: "-0.5px",
          }}
        >
          {step.title}
        </h3>
      )}
      <div
        style={{
          color: "#475569",
          fontWeight: "500",
          fontSize: "16px",
          marginBottom: "30px",
          lineHeight: "1.5",
        }}
      >
        {step.content}
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <button
          {...skipProps}
          className="hover:text-indigo-600 transition-colors"
          style={{
            color: "#94a3b8",
            fontWeight: "700",
            fontSize: "13px",
            textTransform: "uppercase",
            background: "none",
            border: "none",
            cursor: "pointer",
          }}
        >
          Skip Tour
        </button>

        <div style={{ display: "flex", gap: "14px" }}>
          {index > 0 && (
            <button
              {...backProps}
              className="btn-3d-secondary"
              style={{
                backgroundColor: "#f1f5f9",
                border: "none",
                borderRadius: "20px",
                padding: "10px 20px",
                fontWeight: "800",
                color: "#64748b",
                cursor: "pointer",
                boxShadow: "0 4px 0 #cbd5e1",
                transition: "all 0.1s",
              }}
            >
              Back
            </button>
          )}
          <button
            {...primaryProps}
            className="btn-3d-primary"
            style={{
              background: "linear-gradient(180deg, #169a90 0%, #0d7a72 100%)",
              border: "none",
              borderRadius: "20px",
              padding: "10px 24px",
              fontWeight: "800",
              color: "white",
              cursor: "pointer",
              boxShadow: "0 6px 0 #085a54, 0 12px 20px rgba(22, 154, 144, 0.3)",
              transition: "all 0.1s",
            }}
          >
            {isLastStep ? "Next Page" : "Next Step"}
          </button>
        </div>
      </div>
    </div>
  </div>
);

export default function DashboardPage() {
  const Navigate = useNavigate();
  const {
    user,
    goals,
    achievements,
    academicPerformance,
    careerPaths,
    isLoading,
  } = useUserData();
  const [isNotesEditorOpen, setIsNotesEditorOpen] = useState(false);
  const [runTour, setRunTour] = useState(false);
  const currentTheme = useThemeStore((state) => state.getCurrentTheme());

  // Read directly from the Zustand store (populated by useStudentSync in App.jsx)
  const studentData = StudentStore((s) => s.student);

  // Derive HeroBanner journey data from student state + last journey progress
  const heroBannerProps = useMemo(() => {
    const journeyRaw = localStorage.getItem("journey_progress");
    const journey = journeyRaw ? JSON.parse(journeyRaw) : null;

    if (!studentData?.traitProfile) {
      return {
        journeyTitle: "Start your career discovery",
        journeySubtitle: "Take the psychometric assessment to unlock your trait profile and career matches.",
        journeyUrl: "/psychometric-assessment",
      };
    }
    const paths = studentData?.selectedBroadPaths ?? [];
    if (paths.length === 0) {
      return {
        journeyTitle: "Pick your career paths",
        journeySubtitle: "You've mapped your traits — now choose the paths you want to explore.",
        journeyUrl: "/broad-path-selection",
      };
    }
    return {
      journeyTitle: `Explore your career paths`,
      journeySubtitle: `You have ${paths.length} career path${paths.length > 1 ? "s" : ""} — dive deeper into your matches.`,
      journeyUrl: "/career-explorer",
    };
  }, [studentData]);

  const tourSteps = [
    {
      target: "body",
      placement: "center",
      title: "Welcome Home!",
      content:
        "Your personalized dashboard is ready. Let's explore your career journey together!",
      disableBeacon: true,
    },
    {
      target: ".dashboard-header",
      content: "Welcome to your EarlyScape Dashboard",
      placement: "bottom",
    },
    {
      target: ".hero-banner",
      content: "Use this section to continue your learning journey!",
      placement: "bottom",
    },
    {
      target: ".dashboard-stats-grid",
      content:
        "Watch your Stats, XP and Streaks grow in real-time as you learn.",
      placement: "top",
    },
    {
      target: ".tour-career-paths",
      content: "We've mapped out some incredible future paths for you to see.",
      placement: "top",
    },
    {
      target: ".tour-profile-card",
      content: "Your personal progress hub. Keep those goals updated!",
      placement: "left",
    },
    {
      target: ".tour-grades-chart",
      content: "A high-level view of your academic wins this semester.",
      placement: "left",
    },
    {
      target: ".tour-achievements",
      content: "Your trophy cabinet. Time to collect them all!",
      placement: "left",
      locale: { last: "Explore Paths" },
    },
  ];

  useEffect(() => {
    if (!isLoading) {
      const hasTakenTour = localStorage.getItem("hasTakenDashboardTour");

      if (!hasTakenTour) {
        setTimeout(() => setRunTour(true), 1500);
      }
    }
  }, [isLoading]);

  const handleJoyrideCallback = (data) => {
    const { status } = data;
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      localStorage.setItem("hasTakenDashboardTour", "true");
      setRunTour(false);
      if (status === STATUS.FINISHED) Navigate("/career-path");
    }
  };

  const restartTour = () => {
    setRunTour(false);
    setTimeout(() => {
      setRunTour(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 100);
  };

  return (
    <ProtectedRoute>
      <style>{`
        @keyframes boxPopIn {
          0% { transform: scale(0.9) translateY(20px); opacity: 0; }
          100% { transform: scale(1) translateY(0); opacity: 1; }
        }
        .tour-3d-box { animation: boxPopIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1); }
        
        .btn-3d-primary:active {
          transform: translateY(3px);
          box-shadow: 0 2px 0 #085a54, 0 4px 10px rgba(22, 154, 144, 0.3) !important;
        }
        .btn-3d-primary:hover {
          filter: brightness(1.1);
        }
        .btn-3d-secondary:active {
          transform: translateY(2px);
          box-shadow: 0 2px 0 #cbd5e1 !important;
        }
        /* Spotlight styling */
        .react-joyride__spotlight {
          box-shadow: 0 0 0 9999px rgba(99, 102, 241, 0.2), 0 0 30px rgba(22, 154, 144, 0.5) !important;
        }
      `}</style>

      <div
        className="flex min-h-screen"
        style={{ backgroundColor: "var(--theme-background)" }}
      >
        <Sidebar />
        <div className="universal-search-container">
          <UniversalSearch />
        </div>

        <main className="flex-1 lg:ml-64 p-3 sm:p-4 md:p-6 lg:p-8 overflow-y-auto w-full transition-all duration-300">
          <div className="flex justify-between items-start mb-6">
            <div className="hidden md:block dashboard-header flex-1">
              <DashboardHeader
                userName={user.name}
                userImage={user.avatarUrl}
              />
            </div>
            <button
              onClick={restartTour}
              className="ml-4 px-4 py-2 text-sm font-bold rounded-xl border-2 transition-all hover:bg-indigo-50 active:scale-95"
              style={{ borderColor: "#6366f1", color: "#6366f1" }}
            >
              Restart Tour ↺
            </button>
          </div>

          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
              <div className="lg:col-span-2 space-y-6 md:space-y-8">
                <div className="hero-banner">
                  <HeroBanner
                    userName={user.name}
                    themeColors={currentTheme.colors}
                    {...heroBannerProps}
                  />
                </div>
                <div className="dashboard-stats-grid">
                  <DashboardStatsGrid
                    totalPoints={user.totalPoints}
                    tasksCompleted={user.tasksCompleted}
                    streakDays={user.streakDays}
                    level={user.level}
                    themeColors={currentTheme.colors}
                  />
                </div>

                {/* Trait Snapshot — always shown; prompts assessment if not yet taken */}
                <TraitSnapshot traitProfile={studentData?.traitProfile} navigate={Navigate} />

                {/* CSE Career Suggestions */}
                {studentData?.careerSuggestions?.length > 0 && (
                  <CareerSuggestionsPanel
                    suggestions={studentData.careerSuggestions}
                    navigate={Navigate}
                  />
                )}

                {/* Quick Actions */}
                <QuickActions navigate={Navigate} />

                <div className="tour-career-paths">
                  <RecommendedCareerPaths
                    careerMatches={studentData?.careerMatches}
                  />
                </div>
              </div>

              <div className="lg:col-span-1 space-y-6 md:space-y-8">
                <div className="tour-profile-card">
                  <ProfileCard
                    user={user}
                    goals={goals}
                    themeColors={currentTheme.colors}
                  />
                </div>
                <div className="tour-grades-chart">
                  <GradesChart
                    grades={
                      (studentData?.academicResults ?? []).length > 0 &&
                      academicPerformance.subjects.length > 0
                        ? academicPerformance.subjects.map((s) => ({
                            subject: s.name,
                            percentage: s.progress,
                          }))
                        : undefined
                    }
                  />
                </div>
                <UpcomingEvents />

                <div className="tour-achievements">
                  <AchievementsGrid studentProfile={studentData} />
                </div>
              </div>
            </div>
          </div>
        </main>

        <NotesEditorModal
          isOpen={isNotesEditorOpen}
          onClose={() => setIsNotesEditorOpen(false)}
          themeColors={currentTheme.colors}
        />

        <Joyride
          steps={tourSteps}
          run={runTour}
          continuous={true}
          showProgress={true}
          showSkipButton={true}
          callback={handleJoyrideCallback}
          tooltipComponent={Animated3DTooltip}
          floaterProps={{ disableAnimation: true }}
          styles={{
            options: {
              zIndex: 10000,
              overlayColor: "rgba(15, 23, 42, 0.65)",
            },
            spotlight: {
              borderRadius: "24px",
            },
          }}
        />
      </div>
    </ProtectedRoute>
  );
}
